import { z } from 'zod';
import React from 'react';
import { RichTextSchema } from '../richText';
import type { StreamingRichText } from '../richText/streaming';
import type { RichText } from '../richText/interfaces';
import { JSONValueSchema } from '@mutiny-pkg/multiplayer/src/tree/ops/operationSchemas';
import { ThemePropsSchema } from './themeSchema';
import * as HeroIcons from '@heroicons/react/24/outline';
import camelCase from 'camelcase';

export type IconName = keyof typeof HeroIcons;
export const IconNames = Object.keys(HeroIcons) as IconName[];
export const formatToIconName = (hyphenatedString: string) => {
  try {
    const baseName = camelCase(hyphenatedString, { pascalCase: true });
    const iconName = baseName.endsWith('Icon') ? baseName : `${baseName}Icon`;
    return iconName;
  } catch (error) {
    console.warn(`Error formatting icon name: ${error}`);
    return '';
  }
};
// If we need to provide all the possible values to the llm, we can do this
// export const formatToHyphenatedIconName = (pascalString: string) => decamelize(pascalString, { separator: '-' });
// export const HyphenatedIconNames = IconNames.map(formatToHyphenatedIconName);

export enum FlexiblePageTreeNodeType {
  Page = 'page',
  Section = 'section',
  Container = 'container',
  Text = 'text',
  Button = 'button',
  StructuredButton = 'structured-button',
  Image = 'image',
  Icon = 'icon',
  Svg = 'svg',
  Video = 'video',
  Embed = 'embed',
  Dialog = 'dialog',
  TabGroup = 'tab-group',
  TabList = 'tab-list',
  TabPanels = 'tab-panels',
  Disclosure = 'disclosure',
  DisclosureContent = 'disclosure-content',
}

export enum EmbedType {
  URL = 'url',
  Code = 'code',
}

// Node types that support flex layout properties (flexDirection, alignItems, justifyContent, etc.)
const FLEX_LAYOUT_SUPPORTED_TYPES: FlexiblePageTreeNodeType[] = [
  FlexiblePageTreeNodeType.Container,
  FlexiblePageTreeNodeType.Page,
  FlexiblePageTreeNodeType.Section,
];

/**
 * Check if a tree node type supports flex layout properties in its className
 * @param nodeType The FlexiblePageTreeNodeType to check
 * @returns true if the node type supports flex properties, false otherwise
 */
export function supportsFlexProperties(nodeType: FlexiblePageTreeNodeType): boolean {
  return FLEX_LAYOUT_SUPPORTED_TYPES.includes(nodeType);
}

const RichTextOrStringSchema = RichTextSchema.latestVersionSchema.or(z.string());

// Regex to detect disallowed Tailwind breakpoint prefixes (blacklist approach)
// Allows: lg: breakpoint prefix (for desktop-specific overrides) and all non-breakpoint prefixes like hover:, focus:, dark:, etc.
// Blocks: xs:, sm:, md:, xl:, 2xl:, 3xl:, 4xl:, 5xl:, etc.
const DISALLOWED_PREFIX_REGEX = /\b(xs:|sm:|md:|xl:|\d+xl:)/;

// Validated className schema for generation - allows lg: breakpoint prefix for desktop overrides
const ValidatedClassNameSchema = z
  .string()
  .optional()
  .describe(
    'Tailwind CSS classes for styling. Use lg: prefix for desktop-specific overrides (base styles apply to mobile). Example: "text-base lg:text-xl" means text-base on mobile, text-xl on desktop. NEVER use sm:, md:, xl:, 2xl: prefixes.',
  )
  .refine(
    (value) => {
      if (!value) return true; // undefined or empty is valid
      // Reject if any disallowed breakpoint prefixes are found (lg: is allowed)
      return !DISALLOWED_PREFIX_REGEX.test(value);
    },
    {
      message:
        'className contains disallowed Tailwind breakpoint prefixes. Only lg: is allowed for desktop overrides. Never use sm:, md:, xl:, 2xl:, etc.',
    },
  );

// Base schema for all tree node data - includes className and flexible JSONValue properties
const BaseTreeNodeDataSchema = z
  .object({
    className: z.string().optional(),
  })
  .catchall(JSONValueSchema);

// Base schema for all tree nodes - includes id, type, data, and optional children
const BaseTreeNodeSchema = z.object({
  id: z.string(),
  type: z.nativeEnum(FlexiblePageTreeNodeType),
  data: BaseTreeNodeDataSchema,
  children: z.array(z.any()).optional(), // Will be refined in specific schemas
});

// Common schema definitions (without catchall or strict) - shared between standard and generation schemas
const ButtonTreeNodeDataBase = z.object({
  href: RichTextOrStringSchema.optional(),
  text: RichTextOrStringSchema,
  disabled: z.boolean().optional(),
  openInNewTab: z.boolean().optional(),
  variant: z.enum(['primary', 'secondary', 'tertiary', 'outline', 'ghost', 'link']).optional(),
  className: z.string().optional(),
  commandfor: z.string().optional(),
  command: z.enum(['close', 'show', 'show-modal']).optional(),
  role: z.string().optional(),
});

export const ButtonTreeNodeDataSchema = ButtonTreeNodeDataBase.catchall(JSONValueSchema);
export const ButtonTreeNodeDataGenerationSchema = ButtonTreeNodeDataBase.omit({ className: true }).extend({
  type: z.literal(FlexiblePageTreeNodeType.Button).optional(),
  className: ValidatedClassNameSchema,
});

export type ButtonVariant = NonNullable<z.infer<typeof ButtonTreeNodeDataSchema>['variant']>;

const StructuredButtonTreeNodeDataBase = z.object({
  className: z.string().optional(),
  disabled: z.boolean().optional(),
  ariaLabel: z.string().optional(),
  command: z.enum(['close', 'show', 'show-modal', '--toggle', '--show', '--hide']).optional(),
  commandfor: z.string().optional(),
  role: z.string().optional(),
  href: RichTextOrStringSchema.optional(),
  openInNewTab: z.boolean().optional(),
});

export const StructuredButtonTreeNodeDataSchema = StructuredButtonTreeNodeDataBase.catchall(JSONValueSchema);
export const StructuredButtonTreeNodeDataGenerationSchema = StructuredButtonTreeNodeDataBase.omit({
  className: true,
}).extend({
  type: z.literal(FlexiblePageTreeNodeType.StructuredButton).optional(),
  className: ValidatedClassNameSchema,
});

export const VOID_ELEMENTS = [
  'a',
  'area',
  'base',
  'br',
  'col',
  'embed',
  'hr',
  'img',
  'input',
  'link',
  'meta',
  'source',
  'track',
  'wbr',
];

const TextTreeNodeDataBase = z.object({
  as: z
    .custom<keyof React.JSX.IntrinsicElements>()
    .optional()
    .refine((value) => !value || !VOID_ELEMENTS.includes(value), {
      message: 'Void elements are not allowed as container elements.',
    })
    .describe(
      `The HTML element to render as. Defaults to "span". Void elements (${VOID_ELEMENTS.join(', ')}) are not allowed.`,
    ),
  text: RichTextOrStringSchema,
  variant: z.enum(['body', 'heading1', 'heading2', 'heading3', 'heading4', 'overline']).optional(),
  className: z.string().optional(),
});

export const TextTreeNodeDataSchema = TextTreeNodeDataBase.catchall(JSONValueSchema);
export const TextTreeNodeDataGenerationSchema = TextTreeNodeDataBase.omit({ className: true }).extend({
  type: z.literal(FlexiblePageTreeNodeType.Text).optional(),
  className: ValidatedClassNameSchema,
});

export type TextVariant = NonNullable<z.infer<typeof TextTreeNodeDataSchema>['variant']>;

const ContainerTreeNodeDataBase = z.object({
  as: z
    .custom<keyof React.JSX.IntrinsicElements>()
    .optional()
    .refine((value) => !value || !VOID_ELEMENTS.includes(value), {
      message: 'Void elements are not allowed as container elements.',
    })
    .describe(
      `The HTML element to render as. Defaults to "div". Void elements (${VOID_ELEMENTS.join(', ')}) are not allowed.`,
    ),
  backgroundSrc: z.string().optional(),
  className: z.string().optional(),
  decorative: z
    .boolean()
    .optional()
    .describe(
      'Set to true for containers used purely for visual effects (backgrounds, borders, ambient light) with no meaningful content children. Set to false (or omit) for structural containers that hold actual content.',
    ),
  href: RichTextOrStringSchema.optional().describe(
    'Optional URL to make the entire container clickable. When present, the container renders as an anchor element.',
  ),
  openInNewTab: z
    .boolean()
    .optional()
    .describe('Whether the link should open in a new tab. Only applies when href is set.'),
});

export const ContainerTreeNodeDataSchema = ContainerTreeNodeDataBase.catchall(JSONValueSchema);
export const ContainerTreeNodeDataGenerationSchema = ContainerTreeNodeDataBase.omit({ className: true }).extend({
  type: z.literal(FlexiblePageTreeNodeType.Container).optional(),
  className: ValidatedClassNameSchema,
});

export const PageTreeNodeDataSchema = BaseTreeNodeDataSchema.extend({
  pageTitle: z.string().optional(),
  favicon: z.string().optional(),
  designGuidelines: z.string().optional(),
  pageDescription: z.string().optional(),
  pageHeadHtml: z.string().optional(),
  pageBodyHtml: z.string().optional(),
  as: z.custom<keyof React.JSX.IntrinsicElements>().optional(),
}).merge(ThemePropsSchema);

const ImageTreeNodeDataBase = z.object({
  src: RichTextOrStringSchema,
  alt: z.string().optional(),
  className: z.string().optional(),
  imageHref: RichTextOrStringSchema.optional(),
  openInNewTab: z.boolean().optional(),
});

export const ImageTreeNodeDataSchema = ImageTreeNodeDataBase.catchall(JSONValueSchema);
export const ImageTreeNodeDataGenerationSchema = ImageTreeNodeDataBase.omit({ className: true }).extend({
  type: z.literal(FlexiblePageTreeNodeType.Image).optional(),
  className: ValidatedClassNameSchema,
});

const VideoTreeNodeDataBase = z.object({
  src: z.string(),
  alt: z.string().optional(),
  poster: z.string().optional(),
  controls: z.boolean().optional(),
  autoplay: z.boolean().optional(),
  muted: z.boolean().optional(),
  loop: z.boolean().optional(),
  trackSrc: z.string().optional(),
  trackLabel: z.string().optional(),
  trackSrcLang: z.string().optional(),
  className: z.string().optional(),
});

export const VideoTreeNodeDataSchema = VideoTreeNodeDataBase.catchall(JSONValueSchema);
export const VideoTreeNodeDataGenerationSchema = VideoTreeNodeDataBase.omit({ className: true }).extend({
  type: z.literal(FlexiblePageTreeNodeType.Video).optional(),
  className: ValidatedClassNameSchema,
});

const IconTreeNodeDataBase = z.object({
  name: z.string().refine(
    (value) => {
      const formattedName = formatToIconName(value);
      return IconNames.includes(formattedName as IconName);
    },
    (value) => {
      const formattedName = formatToIconName(value);
      return {
        message: `Invalid icon name: "${value}" (formatted to: ${formattedName}). Use hyphenated names like "arrow-right", "check-circle", "user".`,
      };
    },
  ),
  className: z.string().optional(),
  href: RichTextOrStringSchema.optional(),
  openInNewTab: z.boolean().optional(),
});

export const IconTreeNodeDataSchema = IconTreeNodeDataBase.catchall(JSONValueSchema);
export const IconTreeNodeDataGenerationSchema = IconTreeNodeDataBase.omit({ className: true }).extend({
  type: z.literal(FlexiblePageTreeNodeType.Icon).optional(),
  className: ValidatedClassNameSchema,
});

const SvgTreeNodeDataBase = z.object({
  svg: z.string(),
  viewBox: z.string().optional(),
  className: z.string().optional(),
});

export const SvgTreeNodeDataSchema = SvgTreeNodeDataBase.catchall(JSONValueSchema);
export const SvgTreeNodeDataGenerationSchema = SvgTreeNodeDataBase.omit({ className: true }).extend({
  type: z.literal(FlexiblePageTreeNodeType.Svg).optional(),
  className: ValidatedClassNameSchema,
});

const EmbedTreeNodeDataBase = z.object({
  embedType: z.nativeEnum(EmbedType),
  url: z.string().optional(),
  codeSnippet: z.string().optional(),
  className: z.string().optional(),
});

export const EmbedTreeNodeDataSchema = EmbedTreeNodeDataBase.catchall(JSONValueSchema);
export const EmbedTreeNodeDataGenerationSchema = EmbedTreeNodeDataBase.omit({ className: true }).extend({
  type: z.literal(FlexiblePageTreeNodeType.Embed).optional(),
  className: ValidatedClassNameSchema,
});

const DialogTreeNodeDataBase = z.object({
  ariaLabel: z.string().optional(),
  className: z.string().optional(),
  domTargetId: z.string(),
});

export const DialogTreeNodeDataSchema = DialogTreeNodeDataBase.catchall(JSONValueSchema);
export const DialogTreeNodeDataGenerationSchema = DialogTreeNodeDataBase.omit({ className: true }).extend({
  type: z.literal(FlexiblePageTreeNodeType.Dialog).optional(),
  className: ValidatedClassNameSchema,
});

const TabGroupTreeNodeDataBase = z.object({
  className: z.string().optional(),
});

export const TabGroupTreeNodeDataSchema = TabGroupTreeNodeDataBase.catchall(JSONValueSchema);
export const TabGroupTreeNodeDataGenerationSchema = TabGroupTreeNodeDataBase.omit({ className: true }).extend({
  type: z.literal(FlexiblePageTreeNodeType.TabGroup).optional(),
  className: ValidatedClassNameSchema,
});

const TabListTreeNodeDataBase = z.object({
  className: z.string().optional(),
});

export const TabListTreeNodeDataSchema = TabListTreeNodeDataBase.catchall(JSONValueSchema);
export const TabListTreeNodeDataGenerationSchema = TabListTreeNodeDataBase.extend({
  type: z.literal(FlexiblePageTreeNodeType.TabList).optional(),
});

const TabPanelsTreeNodeDataBase = z.object({
  className: z.string().optional(),
});

export const TabPanelsTreeNodeDataSchema = TabPanelsTreeNodeDataBase.catchall(JSONValueSchema);
export const TabPanelsTreeNodeDataGenerationSchema = TabPanelsTreeNodeDataBase.extend({
  type: z.literal(FlexiblePageTreeNodeType.TabPanels).optional(),
});

const DisclosureTreeNodeDataBase = z.object({
  className: z.string().optional(),
});

export const DisclosureTreeNodeDataSchema = DisclosureTreeNodeDataBase.catchall(JSONValueSchema);
export const DisclosureTreeNodeDataGenerationSchema = DisclosureTreeNodeDataBase.omit({ className: true }).extend({
  type: z.literal(FlexiblePageTreeNodeType.Disclosure).optional(),
  className: ValidatedClassNameSchema,
});

const DisclosureContentTreeNodeDataBase = z.object({
  domTargetId: z.string(),
  defaultOpen: z.boolean().optional(),
  className: z.string().optional(),
});

export const DisclosureContentTreeNodeDataSchema = DisclosureContentTreeNodeDataBase.catchall(JSONValueSchema);
export const DisclosureContentTreeNodeDataGenerationSchema = DisclosureContentTreeNodeDataBase.omit({
  className: true,
}).extend({
  type: z.literal(FlexiblePageTreeNodeType.DisclosureContent).optional(),
  className: ValidatedClassNameSchema,
});

const SectionTreeNodeDataBase = z.object({
  backgroundSrc: z.string().optional(),
  className: z.string().optional(),
});

export const SectionTreeNodeDataSchema = SectionTreeNodeDataBase.catchall(JSONValueSchema);
export const SectionTreeNodeDataGenerationSchema = SectionTreeNodeDataBase.omit({ className: true }).extend({
  type: z.literal(FlexiblePageTreeNodeType.Section).optional(),
  className: ValidatedClassNameSchema,
});

export const ButtonTreeNodeSchema = BaseTreeNodeSchema.extend({
  type: z.literal(FlexiblePageTreeNodeType.Button),
  data: ButtonTreeNodeDataSchema,
  children: z.undefined(),
});

// Interactive child nodes - allows leaf nodes and interactive containers (for StructuredButton context)
const InteractiveChildNodeSchema: z.ZodType<any> = z.lazy(() =>
  z.union([
    TextTreeNodeSchema,
    ImageTreeNodeSchema,
    VideoTreeNodeSchema,
    IconTreeNodeSchema,
    SvgTreeNodeSchema,
    InteractiveContainerTreeNodeSchema,
  ]),
);

// Interactive Container - recursively allows only interactive children
const InteractiveContainerTreeNodeSchema: z.ZodType<any> = z.lazy(() =>
  BaseTreeNodeSchema.extend({
    type: z.literal(FlexiblePageTreeNodeType.Container),
    data: ContainerTreeNodeDataSchema,
    children: z.array(InteractiveChildNodeSchema).optional(),
  }),
);

export const StructuredButtonTreeNodeSchema: z.ZodType<StructuredButtonTreeNode> = z.lazy(() =>
  BaseTreeNodeSchema.extend({
    type: z.literal(FlexiblePageTreeNodeType.StructuredButton),
    data: StructuredButtonTreeNodeDataSchema,
    children: z.array(InteractiveChildNodeSchema).optional(),
  }),
);

export const TextTreeNodeSchema = BaseTreeNodeSchema.extend({
  type: z.literal(FlexiblePageTreeNodeType.Text),
  data: TextTreeNodeDataSchema,
  children: z.undefined(),
});

export const ImageTreeNodeSchema = BaseTreeNodeSchema.extend({
  type: z.literal(FlexiblePageTreeNodeType.Image),
  data: ImageTreeNodeDataSchema,
  children: z.undefined(),
});

export const VideoTreeNodeSchema = BaseTreeNodeSchema.extend({
  type: z.literal(FlexiblePageTreeNodeType.Video),
  data: VideoTreeNodeDataSchema,
  children: z.undefined(),
});

export const IconTreeNodeSchema = BaseTreeNodeSchema.extend({
  type: z.literal(FlexiblePageTreeNodeType.Icon),
  data: IconTreeNodeDataSchema,
  children: z.undefined(),
});

export const SvgTreeNodeSchema = BaseTreeNodeSchema.extend({
  type: z.literal(FlexiblePageTreeNodeType.Svg),
  data: SvgTreeNodeDataSchema,
  children: z.undefined(),
});

export const EmbedTreeNodeSchema = BaseTreeNodeSchema.extend({
  type: z.literal(FlexiblePageTreeNodeType.Embed),
  data: EmbedTreeNodeDataSchema,
  children: z.undefined(),
});

export const DialogTreeNodeSchema: z.ZodType<DialogTreeNode> = z.lazy(() =>
  BaseTreeNodeSchema.extend({
    type: z.literal(FlexiblePageTreeNodeType.Dialog),
    data: DialogTreeNodeDataSchema,
    children: z.array(ComponentTreeNodeSchema),
  }),
);

export const TabGroupTreeNodeSchema: z.ZodType<TabGroupTreeNode> = z.lazy(() =>
  BaseTreeNodeSchema.extend({
    type: z.literal(FlexiblePageTreeNodeType.TabGroup),
    data: TabGroupTreeNodeDataSchema,
    children: z.union([
      z.tuple([TabListTreeNodeSchema, TabPanelsTreeNodeSchema]),
      z.tuple([TabPanelsTreeNodeSchema, TabListTreeNodeSchema]),
    ]),
  }),
);

export const TabListTreeNodeSchema: z.ZodType<TabListTreeNode> = z.lazy(() =>
  BaseTreeNodeSchema.extend({
    type: z.literal(FlexiblePageTreeNodeType.TabList),
    data: TabListTreeNodeDataSchema,
    children: z.array(z.union([ButtonTreeNodeSchema, StructuredButtonTreeNodeSchema])),
  }),
);

export const TabPanelsTreeNodeSchema: z.ZodType<TabPanelsTreeNode> = z.lazy(() =>
  BaseTreeNodeSchema.extend({
    type: z.literal(FlexiblePageTreeNodeType.TabPanels),
    data: TabPanelsTreeNodeDataSchema,
    children: z.array(z.lazy(() => ContainerTreeNodeSchema)),
  }),
);

export const DisclosureContentTreeNodeSchema: z.ZodType<DisclosureContentTreeNode> = z.lazy(() =>
  BaseTreeNodeSchema.extend({
    type: z.literal(FlexiblePageTreeNodeType.DisclosureContent),
    data: DisclosureContentTreeNodeDataSchema,
    children: z.array(z.lazy(() => ComponentTreeNodeSchema)).optional(),
  }),
);

export const DisclosureTreeNodeSchema: z.ZodType<DisclosureTreeNode> = z.lazy(() =>
  BaseTreeNodeSchema.extend({
    type: z.literal(FlexiblePageTreeNodeType.Disclosure),
    data: DisclosureTreeNodeDataSchema,
    children: z.tuple([
      StructuredButtonTreeNodeSchema.refine(
        (button) => button.data.command !== undefined && button.data.commandfor !== undefined,
        { message: 'Disclosure trigger must have command and commandfor' },
      ),
      DisclosureContentTreeNodeSchema,
    ]),
  }),
);

export const SectionTreeNodeSchema: z.ZodType<SectionTreeNode> = z.lazy(() =>
  BaseTreeNodeSchema.extend({
    type: z.literal(FlexiblePageTreeNodeType.Section),
    data: SectionTreeNodeDataSchema,
    children: z.array(z.lazy(() => ComponentTreeNodeSchema)).optional(),
  }),
);

export const ContainerTreeNodeSchema: z.ZodType<ContainerTreeNode> = z.lazy(() =>
  BaseTreeNodeSchema.extend({
    type: z.literal(FlexiblePageTreeNodeType.Container),
    data: ContainerTreeNodeDataSchema,
    children: z.array(z.lazy(() => ComponentTreeNodeSchema)).optional(),
  }),
);

// Derive TypeScript types from schemas - these are strict, non-streaming types
export type ButtonTreeNodeData = z.infer<typeof ButtonTreeNodeDataSchema>;
export type TextTreeNodeData = z.infer<typeof TextTreeNodeDataSchema>;
export type ContainerTreeNodeData = z.infer<typeof ContainerTreeNodeDataSchema>;
export type ImageTreeNodeData = z.infer<typeof ImageTreeNodeDataSchema>;
export type IconTreeNodeData = z.infer<typeof IconTreeNodeDataSchema>;
export type PageTreeNodeData = z.infer<typeof PageTreeNodeDataSchema>;
export type VideoTreeNodeData = z.infer<typeof VideoTreeNodeDataSchema>;
export type SvgTreeNodeData = z.infer<typeof SvgTreeNodeDataSchema>;
export type EmbedTreeNodeData = z.infer<typeof EmbedTreeNodeDataSchema>;
export type DialogTreeNodeData = z.infer<typeof DialogTreeNodeDataSchema>;
export type TabGroupTreeNodeData = z.infer<typeof TabGroupTreeNodeDataSchema>;
export type TabListTreeNodeData = z.infer<typeof TabListTreeNodeDataSchema>;
export type TabPanelsTreeNodeData = z.infer<typeof TabPanelsTreeNodeDataSchema>;
export type DisclosureTreeNodeData = z.infer<typeof DisclosureTreeNodeDataSchema>;
export type DisclosureContentTreeNodeData = z.infer<typeof DisclosureContentTreeNodeDataSchema>;
export type SectionTreeNodeData = z.infer<typeof SectionTreeNodeDataSchema>;

export type ButtonTreeNode = z.infer<typeof ButtonTreeNodeSchema>;

export type StructuredButtonTreeNodeData = z.infer<typeof StructuredButtonTreeNodeDataSchema>;

// Interactive Container type - recursively restricts children to interactive types
export type InteractiveContainerTreeNode = {
  id: string;
  type: FlexiblePageTreeNodeType.Container;
  data: ContainerTreeNodeData;
  children?: InteractiveChildNode[];
};

// Union type for all interactive children (leaf nodes use existing types)
export type InteractiveChildNode =
  | TextTreeNode
  | ImageTreeNode
  | VideoTreeNode
  | IconTreeNode
  | SvgTreeNode
  | InteractiveContainerTreeNode;

export type StructuredButtonTreeNode = {
  id: string;
  type: FlexiblePageTreeNodeType.StructuredButton;
  data: StructuredButtonTreeNodeData;
  children?: InteractiveChildNode[];
};

export type TextTreeNode = z.infer<typeof TextTreeNodeSchema>;
export type PageTreeNode = z.infer<typeof PageTreeNodeSchema>;
export type ImageTreeNode = z.infer<typeof ImageTreeNodeSchema>;
export type VideoTreeNode = z.infer<typeof VideoTreeNodeSchema>;
export type IconTreeNode = z.infer<typeof IconTreeNodeSchema>;
export type SvgTreeNode = z.infer<typeof SvgTreeNodeSchema>;
export type EmbedTreeNode = z.infer<typeof EmbedTreeNodeSchema>;

// Define section/container tree node types manually to avoid circular dependency
export type SectionTreeNode = {
  id: string;
  type: FlexiblePageTreeNodeType.Section;
  data: SectionTreeNodeData;
  children?: ComponentTreeNode[];
};

export type ContainerTreeNode = {
  id: string;
  type: FlexiblePageTreeNodeType.Container;
  data: ContainerTreeNodeData;
  children?: ComponentTreeNode[];
};

export type DialogTreeNode = {
  id: string;
  type: FlexiblePageTreeNodeType.Dialog;
  data: DialogTreeNodeData;
  children?: ComponentTreeNode[];
};

export type TabGroupTreeNode = {
  id: string;
  type: FlexiblePageTreeNodeType.TabGroup;
  data: TabGroupTreeNodeData;
  children?: [TabListTreeNode, TabPanelsTreeNode] | [TabPanelsTreeNode, TabListTreeNode];
};

export type TabListTreeNode = {
  id: string;
  type: FlexiblePageTreeNodeType.TabList;
  data: TabListTreeNodeData;
  children?: (ButtonTreeNode | StructuredButtonTreeNode)[];
};

export type TabPanelsTreeNode = {
  id: string;
  type: FlexiblePageTreeNodeType.TabPanels;
  data: TabPanelsTreeNodeData;
  children?: ContainerTreeNode[];
};

export type DisclosureContentTreeNode = {
  id: string;
  type: FlexiblePageTreeNodeType.DisclosureContent;
  data: DisclosureContentTreeNodeData;
  children?: ComponentTreeNode[];
};

export type DisclosureTreeNode = {
  id: string;
  type: FlexiblePageTreeNodeType.Disclosure;
  data: DisclosureTreeNodeData;
  children?: [StructuredButtonTreeNode, DisclosureContentTreeNode];
};

// Define ComponentTreeNode type to avoid implicit any type
export type ComponentTreeNode =
  | ButtonTreeNode
  | StructuredButtonTreeNode
  | TextTreeNode
  | ImageTreeNode
  | VideoTreeNode
  | IconTreeNode
  | SvgTreeNode
  | EmbedTreeNode
  | DialogTreeNode
  | ContainerTreeNode
  | SectionTreeNode
  | TabGroupTreeNode
  | TabListTreeNode
  | TabPanelsTreeNode
  | DisclosureTreeNode
  | DisclosureContentTreeNode;

export const ComponentTreeNodeSchema: z.ZodType<ComponentTreeNode> = z.lazy(() =>
  z.union([
    ButtonTreeNodeSchema,
    StructuredButtonTreeNodeSchema,
    TextTreeNodeSchema,
    ImageTreeNodeSchema,
    VideoTreeNodeSchema,
    IconTreeNodeSchema,
    SvgTreeNodeSchema,
    EmbedTreeNodeSchema,
    DialogTreeNodeSchema,
    ContainerTreeNodeSchema,
    SectionTreeNodeSchema,
    TabGroupTreeNodeSchema,
    TabListTreeNodeSchema,
    TabPanelsTreeNodeSchema,
    DisclosureTreeNodeSchema,
    DisclosureContentTreeNodeSchema,
  ]),
);

// Interactive child generation schema for StructuredButton context
const InteractiveChildNodeGenerationSchema: z.ZodType<any> = z.lazy(() =>
  z.discriminatedUnion('type', [
    z.object({
      type: z.literal(FlexiblePageTreeNodeType.Text),
      data: TextTreeNodeDataGenerationSchema,
    }),
    z.object({
      type: z.literal(FlexiblePageTreeNodeType.Image),
      data: ImageTreeNodeDataGenerationSchema,
    }),
    z.object({
      type: z.literal(FlexiblePageTreeNodeType.Video),
      data: VideoTreeNodeDataGenerationSchema,
    }),
    z.object({
      type: z.literal(FlexiblePageTreeNodeType.Icon),
      data: IconTreeNodeDataGenerationSchema,
    }),
    z.object({
      type: z.literal(FlexiblePageTreeNodeType.Svg),
      data: SvgTreeNodeDataGenerationSchema,
    }),
    z.object({
      type: z.literal(FlexiblePageTreeNodeType.Container),
      data: ContainerTreeNodeDataGenerationSchema,
      children: z.array(z.lazy(() => InteractiveChildNodeGenerationSchema)).optional(),
    }),
  ]),
);

const TabListTreeNodeGenerationSchema = z.object({
  type: z.literal(FlexiblePageTreeNodeType.TabList),
  data: TabListTreeNodeDataGenerationSchema,
  children: z.array(
    z.union([
      z.object({
        type: z.literal(FlexiblePageTreeNodeType.Button),
        data: ButtonTreeNodeDataGenerationSchema.extend({
          role: z.literal('tab'),
        }),
      }),
      z.object({
        type: z.literal(FlexiblePageTreeNodeType.StructuredButton),
        data: StructuredButtonTreeNodeDataGenerationSchema.extend({
          role: z.literal('tab'),
        }),
        children: z.array(InteractiveChildNodeGenerationSchema).optional(),
      }),
    ]),
  ),
});

const TabPanelsTreeNodeGenerationSchema = z.object({
  type: z.literal(FlexiblePageTreeNodeType.TabPanels),
  data: TabPanelsTreeNodeDataGenerationSchema,
  children: z.array(
    z.object({
      type: z.literal(FlexiblePageTreeNodeType.Container),
      data: ContainerTreeNodeDataGenerationSchema,
      children: z.array(z.lazy(() => ComponentTreeNodeGenerationSchema)).optional(),
    }),
  ),
});

export const ComponentTreeNodeGenerationSchema: z.ZodType<any> = z.lazy(() =>
  z.discriminatedUnion('type', [
    z.object({
      type: z.literal(FlexiblePageTreeNodeType.Button),
      data: ButtonTreeNodeDataGenerationSchema,
    }),
    z.object({
      type: z.literal(FlexiblePageTreeNodeType.StructuredButton),
      data: StructuredButtonTreeNodeDataGenerationSchema,
      children: z.array(InteractiveChildNodeGenerationSchema).optional(),
    }),
    z.object({
      type: z.literal(FlexiblePageTreeNodeType.Text),
      data: TextTreeNodeDataGenerationSchema,
    }),
    z.object({
      type: z.literal(FlexiblePageTreeNodeType.Image),
      data: ImageTreeNodeDataGenerationSchema,
    }),
    z.object({
      type: z.literal(FlexiblePageTreeNodeType.Video),
      data: VideoTreeNodeDataGenerationSchema,
    }),
    z.object({
      type: z.literal(FlexiblePageTreeNodeType.Icon),
      data: IconTreeNodeDataGenerationSchema,
    }),
    z.object({
      type: z.literal(FlexiblePageTreeNodeType.Svg),
      data: SvgTreeNodeDataGenerationSchema,
    }),
    z.object({
      type: z.literal(FlexiblePageTreeNodeType.Embed),
      data: EmbedTreeNodeDataGenerationSchema,
    }),
    z.object({
      type: z.literal(FlexiblePageTreeNodeType.Dialog),
      data: DialogTreeNodeDataGenerationSchema,
      children: z.array(z.lazy(() => ComponentTreeNodeGenerationSchema)).optional(),
    }),
    z.object({
      type: z.literal(FlexiblePageTreeNodeType.Container),
      data: ContainerTreeNodeDataGenerationSchema,
      children: z.array(z.lazy(() => ComponentTreeNodeGenerationSchema)).optional(),
    }),
    z.object({
      type: z.literal(FlexiblePageTreeNodeType.Section),
      data: SectionTreeNodeDataGenerationSchema,
      children: z.array(z.lazy(() => ComponentTreeNodeGenerationSchema)).optional(),
    }),
    z.object({
      type: z.literal(FlexiblePageTreeNodeType.TabGroup),
      data: TabGroupTreeNodeDataGenerationSchema,
      children: z.union([
        z.tuple([TabListTreeNodeGenerationSchema, TabPanelsTreeNodeGenerationSchema]),
        z.tuple([TabPanelsTreeNodeGenerationSchema, TabListTreeNodeGenerationSchema]),
      ]),
    }),
    z.object({
      type: z.literal(FlexiblePageTreeNodeType.Disclosure),
      data: DisclosureTreeNodeDataGenerationSchema,
      children: z.tuple([
        z
          .object({
            type: z.literal(FlexiblePageTreeNodeType.StructuredButton),
            data: StructuredButtonTreeNodeDataGenerationSchema,
            children: z.array(InteractiveChildNodeGenerationSchema).optional(),
          })
          .refine((button) => button.data.command !== undefined && button.data.commandfor !== undefined, {
            message: 'Disclosure triggers must have command and commandfor',
          }),
        z.object({
          type: z.literal(FlexiblePageTreeNodeType.DisclosureContent),
          data: DisclosureContentTreeNodeDataGenerationSchema,
          children: z.array(z.lazy(() => ComponentTreeNodeGenerationSchema)).optional(),
        }),
      ]),
    }),
  ]),
);

export type ComponentTreeNodeData =
  | ButtonTreeNodeData
  | StructuredButtonTreeNodeData
  | TextTreeNodeData
  | ImageTreeNodeData
  | VideoTreeNodeData
  | IconTreeNodeData
  | SvgTreeNodeData
  | EmbedTreeNodeData
  | DialogTreeNodeData
  | ContainerTreeNodeData
  | SectionTreeNodeData
  | TabGroupTreeNodeData
  | DisclosureTreeNodeData
  | DisclosureContentTreeNodeData;

export const ComponentTreeNodeDataSchema: z.ZodType<ComponentTreeNodeData> = z.lazy(() =>
  z.union([
    ButtonTreeNodeDataSchema,
    StructuredButtonTreeNodeDataSchema,
    TextTreeNodeDataSchema,
    ImageTreeNodeDataSchema,
    VideoTreeNodeDataSchema,
    IconTreeNodeDataSchema,
    SvgTreeNodeDataSchema,
    EmbedTreeNodeDataSchema,
    DialogTreeNodeDataSchema,
    ContainerTreeNodeDataSchema,
    SectionTreeNodeDataSchema,
    TabGroupTreeNodeDataSchema,
    DisclosureTreeNodeDataSchema,
    DisclosureContentTreeNodeDataSchema,
  ]),
);

export const PageTreeNodeSchema = BaseTreeNodeSchema.extend({
  type: z.literal(FlexiblePageTreeNodeType.Page),
  data: PageTreeNodeDataSchema,
  children: z.array(SectionTreeNodeSchema).optional(),
});

export type FlexiblePageTreeNode = PageTreeNode;

// Base flexible page tree node type - includes type information and is available for any flexible page node
export type BaseFlexiblePageTreeNode = z.infer<typeof BaseTreeNodeSchema>;

// Type guard functions for flexible page tree nodes
export const isTextTreeNode = (node: BaseFlexiblePageTreeNode): node is TextTreeNode => {
  return node.type === FlexiblePageTreeNodeType.Text;
};

export const isButtonTreeNode = (node: BaseFlexiblePageTreeNode): node is ButtonTreeNode => {
  return node.type === FlexiblePageTreeNodeType.Button;
};

export const isStructuredButtonTreeNode = (node: BaseFlexiblePageTreeNode): node is StructuredButtonTreeNode => {
  return node.type === FlexiblePageTreeNodeType.StructuredButton;
};

export const isImageTreeNode = (node: BaseFlexiblePageTreeNode): node is ImageTreeNode => {
  return node.type === FlexiblePageTreeNodeType.Image;
};

export const isContainerTreeNode = (node: BaseFlexiblePageTreeNode): node is ContainerTreeNode => {
  return node.type === FlexiblePageTreeNodeType.Container;
};

export const isSectionTreeNode = (node: BaseFlexiblePageTreeNode): node is SectionTreeNode => {
  return node.type === FlexiblePageTreeNodeType.Section;
};

export const isDialogTreeNode = (node: BaseFlexiblePageTreeNode): node is DialogTreeNode => {
  return node.type === FlexiblePageTreeNodeType.Dialog;
};

export const isVideoTreeNode = (node: BaseFlexiblePageTreeNode): node is VideoTreeNode => {
  return node.type === FlexiblePageTreeNodeType.Video;
};

export const isIconTreeNode = (node: BaseFlexiblePageTreeNode): node is IconTreeNode => {
  return node.type === FlexiblePageTreeNodeType.Icon;
};

export const isSvgTreeNode = (node: BaseFlexiblePageTreeNode): node is SvgTreeNode => {
  return node.type === FlexiblePageTreeNodeType.Svg;
};

export const isEmbedTreeNode = (node: BaseFlexiblePageTreeNode): node is EmbedTreeNode => {
  return node.type === FlexiblePageTreeNodeType.Embed;
};

export const isTabGroupTreeNode = (node: BaseFlexiblePageTreeNode): node is TabGroupTreeNode => {
  return node.type === FlexiblePageTreeNodeType.TabGroup;
};

export const isTabListTreeNode = (node: BaseFlexiblePageTreeNode): node is TabListTreeNode => {
  return node.type === FlexiblePageTreeNodeType.TabList;
};

export const isTabPanelsTreeNode = (node: BaseFlexiblePageTreeNode): node is TabPanelsTreeNode => {
  return node.type === FlexiblePageTreeNodeType.TabPanels;
};

export const isDisclosureTreeNode = (node: BaseFlexiblePageTreeNode): node is DisclosureTreeNode => {
  return node.type === FlexiblePageTreeNodeType.Disclosure;
};

export const isDisclosureContentTreeNode = (node: BaseFlexiblePageTreeNode): node is DisclosureContentTreeNode => {
  return node.type === FlexiblePageTreeNodeType.DisclosureContent;
};

const typeLabelMap: Record<FlexiblePageTreeNodeType, string> = {
  [FlexiblePageTreeNodeType.Page]: 'Page',
  [FlexiblePageTreeNodeType.Section]: 'Section',
  [FlexiblePageTreeNodeType.Container]: 'Container',
  [FlexiblePageTreeNodeType.Text]: 'Text',
  [FlexiblePageTreeNodeType.Button]: 'Button',
  [FlexiblePageTreeNodeType.StructuredButton]: 'Button',
  [FlexiblePageTreeNodeType.Image]: 'Image',
  [FlexiblePageTreeNodeType.Icon]: 'Icon',
  [FlexiblePageTreeNodeType.Svg]: 'SVG',
  [FlexiblePageTreeNodeType.Video]: 'Video',
  [FlexiblePageTreeNodeType.Embed]: 'Embed',
  [FlexiblePageTreeNodeType.Dialog]: 'Modal',
  [FlexiblePageTreeNodeType.TabGroup]: 'Tab group',
  [FlexiblePageTreeNodeType.TabList]: 'Tab list',
  [FlexiblePageTreeNodeType.TabPanels]: 'Tab panels',
  [FlexiblePageTreeNodeType.Disclosure]: 'Accordion Panel',
  [FlexiblePageTreeNodeType.DisclosureContent]: 'Accordion content',
};

export const getNodeLabel = (nodeType: FlexiblePageTreeNodeType): string => {
  return typeLabelMap[nodeType] || 'Unknown';
};

// Type-to-schema mapping for runtime validation
// Maps each node type to its corresponding generation schema for type-aware update validation
export const NodeTypeToGenerationSchemaMap: Record<FlexiblePageTreeNodeType, z.ZodType<unknown>> = {
  [FlexiblePageTreeNodeType.Button]: ButtonTreeNodeDataGenerationSchema,
  [FlexiblePageTreeNodeType.StructuredButton]: StructuredButtonTreeNodeDataGenerationSchema,
  [FlexiblePageTreeNodeType.Text]: TextTreeNodeDataGenerationSchema,
  [FlexiblePageTreeNodeType.Container]: ContainerTreeNodeDataGenerationSchema,
  [FlexiblePageTreeNodeType.Image]: ImageTreeNodeDataGenerationSchema,
  [FlexiblePageTreeNodeType.Video]: VideoTreeNodeDataGenerationSchema,
  [FlexiblePageTreeNodeType.Icon]: IconTreeNodeDataGenerationSchema,
  [FlexiblePageTreeNodeType.Svg]: SvgTreeNodeDataGenerationSchema,
  [FlexiblePageTreeNodeType.Embed]: EmbedTreeNodeDataGenerationSchema,
  [FlexiblePageTreeNodeType.Section]: SectionTreeNodeDataGenerationSchema,
  [FlexiblePageTreeNodeType.Dialog]: DialogTreeNodeDataGenerationSchema,
  [FlexiblePageTreeNodeType.TabGroup]: TabGroupTreeNodeDataGenerationSchema,
  [FlexiblePageTreeNodeType.TabList]: TabListTreeNodeDataGenerationSchema,
  [FlexiblePageTreeNodeType.TabPanels]: TabPanelsTreeNodeDataGenerationSchema,
  [FlexiblePageTreeNodeType.Disclosure]: DisclosureTreeNodeDataGenerationSchema,
  [FlexiblePageTreeNodeType.DisclosureContent]: DisclosureContentTreeNodeDataGenerationSchema,
  [FlexiblePageTreeNodeType.Page]: PageTreeNodeDataSchema,
};

/**
 * Get the generation schema for a node type. Used for type-aware validation of update actions.
 * @param nodeType The FlexiblePageTreeNodeType to get the schema for
 * @returns The Zod schema for the node type, or null if unknown
 */
export function getGenerationSchemaForNodeType(nodeType: FlexiblePageTreeNodeType): z.ZodType<unknown> | null {
  return NodeTypeToGenerationSchemaMap[nodeType] ?? null;
}

// =============================================================================
// Streaming Types
// =============================================================================
// During JSON streaming, tree nodes are built incrementally:
// - Children arrays may contain undefined slots (placeholders)
// - Data properties may not have arrived yet
// - RichText fields may be partially populated
// - The node itself might be undefined
//
// These types make streaming handling explicit in components.

/**
 * Type for RichText props that may receive streaming (partial) data at runtime.
 */
export type RichTextOrStreamingOrString = RichText | StreamingRichText | string;

/**
 * Utility type that replaces RichText | string fields with RichTextOrStreamingOrString.
 * This allows tree node data types to accept partially-streamed RichText at runtime.
 *
 * Only affects fields where RichText is part of the type union (e.g., RichText | string).
 * Plain string fields (like className) are left unchanged.
 */
type WithStreamingRichText<T> = {
  [K in keyof T]: T[K] extends RichText | string
    ? RichText extends T[K]
      ? RichTextOrStreamingOrString
      : T[K]
    : T[K] extends (RichText | string) | undefined
    ? RichText extends NonNullable<T[K]>
      ? RichTextOrStreamingOrString | undefined
      : T[K]
    : T[K];
};

// Streaming data types:
// - WithStreamingRichText allows RichText fields to accept partial/streaming data
// - Partial makes all properties optional (data may not have arrived yet)
export type StreamingTextTreeNodeData = Partial<WithStreamingRichText<TextTreeNodeData>>;
export type StreamingButtonTreeNodeData = Partial<WithStreamingRichText<ButtonTreeNodeData>>;
export type StreamingStructuredButtonTreeNodeData = Partial<WithStreamingRichText<StructuredButtonTreeNodeData>>;
export type StreamingContainerTreeNodeData = Partial<WithStreamingRichText<ContainerTreeNodeData>>;
export type StreamingImageTreeNodeData = Partial<WithStreamingRichText<ImageTreeNodeData>>;
export type StreamingIconTreeNodeData = Partial<WithStreamingRichText<IconTreeNodeData>>;
export type StreamingVideoTreeNodeData = Partial<VideoTreeNodeData>;
export type StreamingSvgTreeNodeData = Partial<SvgTreeNodeData>;
export type StreamingEmbedTreeNodeData = Partial<EmbedTreeNodeData>;
export type StreamingDialogTreeNodeData = Partial<DialogTreeNodeData>;
export type StreamingSectionTreeNodeData = Partial<SectionTreeNodeData>;
export type StreamingTabGroupTreeNodeData = Partial<TabGroupTreeNodeData>;
export type StreamingTabListTreeNodeData = Partial<TabListTreeNodeData>;
export type StreamingTabPanelsTreeNodeData = Partial<TabPanelsTreeNodeData>;
export type StreamingDisclosureTreeNodeData = Partial<DisclosureTreeNodeData>;
export type StreamingDisclosureContentTreeNodeData = Partial<DisclosureContentTreeNodeData>;

// Union of all streaming data types
export type StreamingComponentTreeNodeData =
  | StreamingTextTreeNodeData
  | StreamingButtonTreeNodeData
  | StreamingStructuredButtonTreeNodeData
  | StreamingContainerTreeNodeData
  | StreamingImageTreeNodeData
  | StreamingVideoTreeNodeData
  | StreamingSvgTreeNodeData
  | StreamingIconTreeNodeData
  | StreamingEmbedTreeNodeData
  | StreamingDialogTreeNodeData
  | StreamingSectionTreeNodeData
  | StreamingTabGroupTreeNodeData
  | StreamingTabListTreeNodeData
  | StreamingTabPanelsTreeNodeData
  | StreamingDisclosureTreeNodeData
  | StreamingDisclosureContentTreeNodeData;

/**
 * A tree node during streaming. The structure may be partially populated:
 * - `id` and `type` are always present (assigned when node is created)
 * - `data` properties may be undefined (not yet streamed)
 * - `children` may contain undefined slots
 */
export type StreamingComponentTreeNode = {
  id: string;
  type: FlexiblePageTreeNodeType;
  data?: StreamingComponentTreeNodeData;
  children?: (StreamingComponentTreeNode | undefined)[];
};

/**
 * Type guard to check if a streaming node slot is defined (not undefined/null).
 */
export function isDefinedNode(node: StreamingComponentTreeNode | undefined | null): node is StreamingComponentTreeNode {
  return node != null;
}
